/**
 * @fileoverview One plant, opened up, from wherever you were looking at it small.
 *
 * The tile and the row are deliberately thin — a picture, a name, a bed — and
 * that leaves them with no way to reach anything about the plant itself. You
 * could see twenty plants and act on none of them: no photographs, no journal,
 * no way to move a picture onto the right plant.
 *
 * What opens is the card. Not a reduced version of it, not a new arrangement
 * to learn — the same component the cards view renders, in a dialog. A tile is
 * a card in less space, so expanding one gives back exactly the space.
 *
 * Editing belongs to this component rather than to whoever opens it. It used
 * to arrive as an onEdit prop, which the garden passed and the locations page
 * did not — so the same plant, opened from its bed instead of from the grid,
 * came up with no pencil on it and nothing you could change. A capability
 * every caller has to remember to hand over is one some caller will not. There
 * is nothing to hand over now.
 *
 * Where: PlantGrid (a tile or a row opened), LocationGrid, SeasonPage and
 * WeatherPage.
 * API: through PlantsContext: edit (POST /plants/update) and archive
 * (PATCH /plants/:id/status); Merge into... (in the Edit form's Archive panel, for a plant with twins) and the prompt after an
 * edit that made twins are the merge sheet's (D66, lib/useMergeFlow).
 * Settings: none.
 * Words (en/es): plants.form.saveFailed, plants.form.removeFailed; the
 * card's and the form's own.
 * Watch out: an archived plant (status 0) opens read-only.
 *
 * @module PlantQuickLook
 */

import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Plant } from '@/shared/types/plant';
import type { PlantWritePayload } from '@/shared/api/plantCatalogue';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { PlantCard } from './PlantCard';
import { PlantFormDialog } from './dialogs/PlantFormDialog';
import { MergePlantSheet } from './dialogs/MergePlantSheet';
import { useMergeFlow } from '../lib/useMergeFlow';
import { plantShown } from '@/shared/lib/plantName';

/** Which plant is open, and how to close it. */
interface PlantQuickLookProps {
  /** The plant to open, or null when nothing is open. */
  plant: Plant | null;
  /** Closes the dialog. */
  onClose: () => void;
}

/**
 * The plant's card in a dialog, and the edit form it opens.
 *
 * @param props - PlantQuickLookProps.
 */
export const PlantQuickLook: React.FC<PlantQuickLookProps> = ({ plant, onClose }) => {
  const { editPlant, archivePlant } = usePlantsContext();
  const { reactTranslate: t } = useTranslation();
  /** Merge into... (D66): the sheet's state, and the prompt raised after an edit that made twins. */
  const merge = useMergeFlow();

  /*
   * The plant being edited, held here rather than read from `plant`.
   *
   * Opening the form closes the quick look, as it always did from the garden —
   * two stacked dialogs is a worse answer than one. So `plant` goes null at
   * the moment the form opens, and the form needs its own copy of what it is
   * editing.
   */
  const [editing, setEditing] = React.useState<Plant | null>(null);

  /**
   * Opens the edit form for the plant, closing the quick look behind it.
   *
   * @param target - The plant.
   */
  const openEdit = (target: Plant): void => {
    setEditing(target);
    onClose();
  };

  /**
   * Saves the form's changes to the plant; a failure is thrown for the form to show.
   *
   * @param payload - Its variety and bed.
   * @param plantObjId - The plant (never null here).
   */
  const handleSave = async (
    payload: PlantWritePayload,
    plantObjId: number | null,
  ): Promise<void> => {
    // Null means "create", which the garden offers and this does not: there is
    // no way to reach here without a plant already open.
    if (plantObjId === null) throw new Error(t('plants.form.saveFailed'));
    const result = await editPlant(plantObjId, payload);
    if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
    merge.rememberEdit(plantObjId, result);
  };

  /**
   * Archives the plant from the form; a failure is thrown for the form to show.
   *
   * @param plantObjId - The plant.
   */
  const handleRemove = async (plantObjId: number): Promise<void> => {
    const result = await archivePlant(plantObjId);
    if (!result.success) throw new Error(result.error ?? t('plants.form.removeFailed'));
  };

  return (
    <>
      <Dialog open={!!plant} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent scrollBody={false} className="flex max-h-[92dvh] w-[calc(100%-1.5rem)] flex-col overflow-hidden p-0 sm:max-w-md">
          {/*
            * The card carries its own name, so the dialog's title would say it
            * twice. Screen readers still need one, hence the sr-only heading.
            */}
          <DialogTitle className="sr-only">{plantShown(plant)}</DialogTitle>
          <div className="flex-1 overflow-y-auto">
            {/*
              * The card sizes itself to a grid column and caps at max-w-sm; in here
              * the dialog is the column, so let it fill. Its name row stops short
              * of the dialog's close button, which sat on top of the card's name row.
              */}
            {plant && (
              <div className="[&>div]:max-w-none [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none [&_[data-card-head]]:pr-12">
                {/*
                  * The same card the garden draws (its trays open one at a time, as there): you have already said which
                  * plant you want, by tapping it, so nothing here is folded behind a second tap.
                  */}
                <PlantCard
                  plant={plant}
                  // An archived plant opens as the archive shows it: no pencil,
                  // nothing that writes, no forecast. It used to open as if it
                  // were still in the garden, from the grid and the list alike.
                  onEdit={plant.status === 0 ? undefined : openEdit}
                  archived={plant.status === 0}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MergePlantSheet {...merge.sheetProps} />

      <PlantFormDialog
        plant={editing}
        isOpen={!!editing}
        onClose={() => { setEditing(null); merge.afterFormClosed(); }}
        onSave={handleSave}
        onRemove={handleRemove}
        // Merge into... is in the form's Archive panel for a plant with twins: the form closes and the sheet opens.
        onMerge={(p) => { setEditing(null); merge.openMerge(p); }}
      />
    </>
  );
};
